import { Flex, useBreakpointValue } from '@chakra-ui/react';
import type { StatsMetricValue } from '@ui/hooks/stats/types';
import React from 'react';

import StatsChartPlaceholder from './StatsChartPlaceholder';
import StatsChartTitle from './StatsChartTitle';
import { StatsColumnChart } from './StatsColumnChart';
import StatsPieChart from './StatsPieChart';
import { StatsSimpleLineChart } from './StatsSimpleLineChart';

export type StatsChartProps = {
  values: Record<string, StatsMetricValue[] | undefined>;
  alternativeValues?: Record<string, StatsMetricValue[] | undefined>;
  units?: string[];
  title: string;
  type: 'line' | 'bar' | 'pie';
};

function StatsChart({
  values,
  title,
  type,
  alternativeValues,
  units,
}: StatsChartProps) {
  const placeholderHeight = useBreakpointValue({
    base: type !== 'pie' ? 243 : 210,
    desktop: type !== 'pie' ? 289 : 170,
  });

  if (
    Object.keys(values).filter((key) => values[key] && !values[key]?.length)
      .length === Object.keys(values).length
  ) {
    return (
      <Flex w="full" flexDir="column" minH={placeholderHeight}>
        <StatsChartTitle text={title} color="text-light" />
        <Flex
          justify="center"
          align="center"
          flex={1}
          textStyle="h4"
          color="warning"
        >
          No data
        </Flex>
      </Flex>
    );
  }

  if (
    Object.keys(values).filter((key) => !values[key]?.length).length ===
    Object.keys(values).length
  ) {
    return <StatsChartPlaceholder height={placeholderHeight} />;
  }

  const props = {
    minH: placeholderHeight,
    values,
    title,
    alternativeValues,
    units,
  };

  ensureConsistentStart(values);
  if (type === 'line') return <StatsSimpleLineChart {...props} />;
  if (type === 'bar') return <StatsColumnChart {...props} />;
  if (type === 'pie') return <StatsPieChart {...props} />;

  return <>Chart definition invalid</>;
}

const ensureConsistentStart = (
  values: Record<string, StatsMetricValue[] | undefined>,
) => {
  let earliestTimestamp = Infinity;
  for (const key in values) {
    const keyValues = values[key];
    // don't do anything if data not yet loaded
    if (!keyValues) return values;

    if (!keyValues.length) {
      delete values[key];
    }

    const firstItem = keyValues[0];
    if (firstItem && Number(firstItem.timestamp) < earliestTimestamp) {
      earliestTimestamp = Number(firstItem.timestamp);
    }
  }

  if (earliestTimestamp === Infinity) return values;

  for (const key in values) {
    const keyValues = values[key] as StatsMetricValue[];
    if (!keyValues.length) continue;
    const keyValuesStart = Number(keyValues[0].timestamp);
    if (keyValuesStart === earliestTimestamp) continue;

    const nZeros = Math.floor((keyValuesStart - earliestTimestamp) / 86400);
    // push "0" values to the beginning for all missing data
    const zeros = [...new Array(nZeros)];
    values[key] = [
      ...zeros.map((_, i) => ({
        value: 0,
        timestamp: `${earliestTimestamp + i * 86400}`,
      })),
      ...keyValues,
    ];
  }
};

export default StatsChart;
